/**
=========================================================

* Tazi Rides Ltd has developed this dashboard based on a further configured version of the original template.
* Website: https://www.tazirides.co.uk
* All rights reserved.

=========================================================
* Material Dashboard 2 PRO React TS - v1.0.2
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-2-pro-react-ts
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// @material-ui core components
import React, { useContext, useMemo } from 'react'

import { Checkbox, Icon } from '@mui/material'
import Autocomplete from '@mui/material/Autocomplete'
import Card from '@mui/material/Card'
import Grid from '@mui/material/Grid'
import { useNavigate } from 'react-router-dom'

import MDAvatar from 'components/MDAvatar'
import MDBox from 'components/MDBox'
import MDButton from 'components/MDButton'
import MDTypography from 'components/MDTypography'
import { ROLE_LEVEL } from 'constants/roles'
import { useAuth } from 'context/auth'
import { useMaterialUIController } from 'context/materialUI'
import { Feedback } from 'layouts/feedback/internal/types'
import FormField from 'modules/FormField'
import { ROUTE_PATHS } from 'routes'
import { toTitleCase } from 'utils/string'

import { PREFERRED_CONTACT_TYPE_OPTIONS } from '../../../constants'
import { FormContext } from '../../constants'

function Customer(): JSX.Element {
	const [controller] = useMaterialUIController()
	const { user } = useAuth()
	const formContext = useContext(FormContext)
	const navigate = useNavigate()
	const { localForm, setFormEdited, setLocalForm, isNew, showError } = formContext
	const { businessAccounts } = controller

	const STAGE = 1
	const sectionDisabled = !ROLE_LEVEL[2].includes(user.data.role) && localForm.stage.current !== 1

	const businessAccountRefncIds = useMemo(
		() => businessAccounts?.map(account => account.referenceId),
		[businessAccounts]
	)
	const businessAccountNames = businessAccounts?.map(account => account.name)

	const accountRefncIdMapping = useMemo(
		() =>
			Object.fromEntries(
				businessAccounts?.map(account => [account.referenceId, { ...account }]) ?? []
			),
		[businessAccounts]
	)

	const accountNameMapping = useMemo(
		() =>
			Object.fromEntries(businessAccounts?.map(account => [account.name, { ...account }]) ?? []),
		[businessAccounts]
	)

	const handleFormUpdate = (value: string, field: keyof Feedback['form']['customer']) => {
		setLocalForm(prev => ({
			...prev,
			form: {
				...prev.form,
				customer: {
					...prev.form.customer,
					[field]: value,
				},
			},
		}))
		setFormEdited(true)
	}

	return (
		<Card id="provider" sx={{ overflow: 'visible' }}>
			<MDBox display={'flex'} flexDirection={'row'} gap={2} p={3}>
				<MDAvatar bgColor="primary" size="xs">
					2
				</MDAvatar>
				<MDTypography variant="h5" fontWeight="medium">
					Customer Details
				</MDTypography>
			</MDBox>
			<MDBox component="form" pb={3} px={3}>
				<Grid container spacing={3}>
					<Grid item xs={12} sm={6}>
						<FormField
							disabled={sectionDisabled}
							value={localForm.form.customer.name}
							label="Name"
							onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
								handleFormUpdate(e.target.value, 'name')
							}}
							error={showError && !localForm.form.customer.name.trim()}
							helperText={
								showError && !localForm.form.customer.name.trim() ? 'Name is required' : ''
							}
						/>
					</Grid>

					<Grid item xs={12} sm={6} md={3}>
						<Autocomplete
							readOnly={sectionDisabled}
							value={toTitleCase(localForm.form.customer.preferredContactType)}
							options={PREFERRED_CONTACT_TYPE_OPTIONS.map(c => toTitleCase(c))}
							disableClearable
							renderInput={params => (
								<FormField
									{...params}
									label="Preferred Contact"
									InputLabelProps={{ shrink: true }}
									error={
										showError && !localForm.form.customer.preferredContactType && !sectionDisabled
									}
									helperText={
										showError && !localForm.form.customer.preferredContactType && !sectionDisabled
											? 'Preferred contact type is required'
											: ''
									}
								/>
							)}
							onChange={(_, newValue) => {
								handleFormUpdate(newValue.toUpperCase(), 'preferredContactType')
							}}
						/>
					</Grid>
					<Grid item xs={12} sm={6} md={3}>
						<FormField
							disabled={sectionDisabled}
							label="Preferred Date/Time"
							inputProps={{ type: 'datetime-local' }}
							value={localForm.form.customer.preferredDatetime}
							onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
								handleFormUpdate(e.target.value, 'preferredDatetime')
							}}
						/>
					</Grid>

					<Grid item xs={12} sm={6}>
						<FormField
							label="Email"
							placeholder="example@email.com"
							inputProps={{ type: 'email' }}
							autoComplete="off"
							disabled={sectionDisabled}
							value={localForm.form?.customer.email}
							onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
								handleFormUpdate(e.target.value, 'email')
							}}
							error={
								showError &&
								(!localForm.form.customer.email.trim() ||
									!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(localForm.form.customer.email))
							}
							helperText={
								showError && !localForm.form.customer.email.trim()
									? 'Email is required'
									: showError &&
										  !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(
												localForm.form.customer.email
										  )
										? 'Enter a valid email address'
										: ''
							}
						/>
					</Grid>
					<Grid item xs={12} sm={6}>
						<FormField
							label="Phone Number"
							placeholder="+44735631620"
							disabled={sectionDisabled}
							value={localForm.form.customer.phoneNumber}
							onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
								handleFormUpdate(e.target.value, 'phoneNumber')
							}}
							error={showError && !localForm.form.customer.phoneNumber.trim()}
							helperText={
								showError && !localForm.form.customer.phoneNumber.trim()
									? 'Phone number is required'
									: ''
							}
						/>
					</Grid>

					<Grid item xs={12} md={3}>
						<Autocomplete
							readOnly={sectionDisabled}
							value={localForm.form.customer.account?.referenceId ?? null}
							options={businessAccountRefncIds}
							noOptionsText="No accounts set up"
							renderInput={params => (
								<FormField
									{...params}
									label="Account ID (Optional)"
									InputLabelProps={{ shrink: true }}
								/>
							)}
							onChange={(_, newValue) => {
								const accountDetails = accountRefncIdMapping[newValue]
								setLocalForm(prev => ({
									...prev,
									form: {
										...prev.form,
										customer: {
											...prev.form.customer,
											account: {
												...accountDetails,
												referenceId: newValue,
											},
										},
									},
								}))
							}}
						/>
					</Grid>
					<Grid item xs={12} md={5}>
						<Autocomplete
							readOnly={sectionDisabled}
							value={localForm.form.customer.account?.name ?? null}
							options={businessAccountNames}
							noOptionsText="No accounts set up"
							renderInput={params => (
								<FormField
									{...params}
									label="Account Name(Optional)"
									InputLabelProps={{ shrink: true }}
								/>
							)}
							onChange={(_, newValue) => {
								const accountDetails = accountNameMapping[newValue]
								setLocalForm(prev => ({
									...prev,
									form: {
										...prev.form,
										customer: {
											...prev.form.customer,
											account: {
												...accountDetails,
												name: newValue,
											},
										},
									},
								}))
							}}
						/>
					</Grid>
					{!isNew && localForm.form.customer.account && (
						<Grid item xs={12} sm={4} alignItems={'center'} display="flex">
							<MDButton
								variant="text"
								color="info"
								onClick={() => {
									navigate(
										`${ROUTE_PATHS.BUSINESS_ACCOUNTS.BASE}/${localForm.form.customer.account.id}`
									)
								}}
							>
								View Account Profile&nbsp;&nbsp;&nbsp;
								<Icon>arrow_circle_right</Icon>
							</MDButton>
						</Grid>
					)}
					<Grid item xs={12}>
						<MDBox
							display="flex"
							alignItems="center"
							justifyContent="flex-end"
							flexDirection={'row'}
						>
							<MDTypography variant="h6" color="secondary">
								Details Complete:
							</MDTypography>
							<Checkbox
								checked={localForm.stage.current > STAGE}
								disabled={localForm.stage.min > STAGE}
								onChange={() => {
									const increment = localForm.type === 'COMMENDATION' ? 2 : 1
									setLocalForm(prev => ({
										...prev,
										stage: {
											...prev.stage,
											current: localForm.stage.current > STAGE ? STAGE : STAGE + increment,
										},
									}))
									setFormEdited(true)
								}}
							/>
						</MDBox>
					</Grid>
				</Grid>
			</MDBox>
		</Card>
	)
}

export default Customer
